// src/services/doctorCare.js
import { api } from "../lib/patientApi";
import { getPatientAccessToken } from "../lib/patientAuth";

function apiBaseUrl() {
  const base = api?.defaults?.baseURL || window.location.origin;
  try {
    return new URL(base, window.location.origin).toString().replace(/\/$/, "");
  } catch {
    return window.location.origin;
  }
}

export function doctorChatSocketUrl(doctorId, threadId = null) {
  const base = apiBaseUrl();
  const wsBase = base.startsWith("https://")
    ? `wss://${base.slice("https://".length)}`
    : base.startsWith("http://")
      ? `ws://${base.slice("http://".length)}`
      : base;

  const token = getPatientAccessToken();
  if (!token) return "";

  const path = threadId
    ? `/api/v1/patients/me/doctors/${doctorId}/chat/threads/${threadId}/ws`
    : `/api/v1/patients/me/doctors/${doctorId}/chat/ws`;

  return `${wsBase}${path}?token=${encodeURIComponent(token)}`;
}

export async function putFile(file, uploadUrl, contentType, headers = {}) {
  const res = await fetch(uploadUrl, {
    method: "PUT",
    headers: Object.keys(headers || {}).length
      ? headers
      : { "Content-Type": contentType || file.type || "application/octet-stream" },
    body: file,
  });

  if (!res.ok) {
    throw new Error("Failed to upload file.");
  }
}

export const doctorCareApi = {
  async chat(doctorId) {
    const res = await api.get(`/api/v1/patients/me/doctors/${doctorId}/chat`);
    return res.data;
  },

  async chatThreads(doctorId) {
    const res = await api.get(`/api/v1/patients/me/doctors/${doctorId}/chat/threads`);
    return res.data;
  },

  async createChatThread(doctorId, payload = {}) {
    const res = await api.post(`/api/v1/patients/me/doctors/${doctorId}/chat/threads`, payload);
    return res.data;
  },

  async chatThread(doctorId, threadId) {
    const res = await api.get(`/api/v1/patients/me/doctors/${doctorId}/chat/threads/${threadId}`);
    return res.data;
  },

  async sendMessage(doctorId, payload, threadId = null) {
    const url = threadId
      ? `/api/v1/patients/me/doctors/${doctorId}/chat/threads/${threadId}/messages`
      : `/api/v1/patients/me/doctors/${doctorId}/chat/messages`;
    const res = await api.post(url, payload);
    return res.data;
  },

  async presignAttachment(doctorId, file, purpose = "chat_attachment") {
    const res = await api.post(`/api/v1/patients/me/doctors/${doctorId}/chat/attachments/presigned-upload`, {
      file_name: file?.name || "attachment",
      content_type: file?.type || "application/octet-stream",
      size_bytes: file?.size || undefined,
      purpose,
    });
    return res.data;
  },

  async uploadAttachment(doctorId, file) {
    const presigned = await this.presignAttachment(doctorId, file, "chat_attachment");
    await putFile(file, presigned.upload_url, presigned.content_type, presigned.upload_headers);
    return {
      file_key: presigned.file_key,
      file_name: file.name || "attachment",
      content_type: presigned.content_type || file.type || "application/octet-stream",
      size_bytes: file.size || undefined,
    };
  },

  async reports(doctorId) {
    const res = await api.get(`/api/v1/patients/me/doctors/${doctorId}/reports`);
    return res.data;
  },

  async presignReport(doctorId, file) {
    const res = await api.post(`/api/v1/patients/me/doctors/${doctorId}/reports/upload-url`, {
      file_name: file?.name || "report",
      content_type: file?.type || "application/octet-stream",
      size_bytes: file?.size || undefined,
      purpose: "reports",
    });
    return res.data;
  },

  async uploadAndShareReport(doctorId, file, payload = {}) {
    const presigned = await this.presignReport(doctorId, file);
    await putFile(file, presigned.upload_url, presigned.content_type, presigned.upload_headers);
    const res = await api.post(`/api/v1/patients/me/doctors/${doctorId}/reports`, {
      ...payload,
      file_key: presigned.file_key,
      file_name: file.name || "report",
      content_type: presigned.content_type || file.type || "application/octet-stream",
      size_bytes: file.size || undefined,
    });
    return res.data;
  },

  async renameReport(doctorId, reportId, payload = {}) {
    const res = await api.patch(`/api/v1/patients/me/doctors/${doctorId}/reports/${reportId}`, payload);
    return res.data;
  },

  async deleteReport(doctorId, reportId) {
    const res = await api.delete(`/api/v1/patients/me/doctors/${doctorId}/reports/${reportId}`);
    return res.data;
  },
};
